import { supabase } from './supabase'
import { todayIso } from './dates'

/**
 * Direct debits, after the fact.
 *
 * These collect themselves, so a "pay now" button would be wrong — but a
 * direct debit that quietly fails is the most dangerous kind of unpaid bill,
 * because nothing about it looks unusual. Nobody chases it, no reminder
 * arrives until the supplier gives up, and the first sign is a letter.
 *
 * So the app asks, the day after: was this collected? One tap either way, and
 * both answers are recorded as facts in the same ledger as everything else —
 * `dd_confirmed` settles it, `dd_failed` puts it back in your face.
 */

export interface AwaitingCollection {
  instalmentId: string
  obligationId: string
  spaceId: string
  description: string
  amountCents: number
  dueDate: string
}

export async function loadAwaitingCollection(userId: string): Promise<AwaitingCollection[]> {
  const { data, error } = await supabase.rpc('awaiting_collection', { p_user_id: userId })
  if (error) throw error

  return (data ?? []).map((row: Record<string, unknown>) => ({
    instalmentId: row.instalment_id as string,
    obligationId: row.obligation_id as string,
    spaceId: row.space_id as string,
    description: (row.description as string) ?? 'Domiciliëring',
    amountCents: Number(row.amount_cents),
    dueDate: row.due_date as string,
  }))
}

export async function confirmCollected(
  item: AwaitingCollection,
  collected: boolean,
  /** Unused — the database reads auth.uid(). Kept so callers need not change. */
  _userId?: string,
): Promise<void> {
  // One transaction, like every other ledger write (rule 9). A confirmed
  // direct debit is money; a failed one is a statement about money. Both are
  // worth the record of who said so.
  const { error } = await supabase.rpc('record_payment', {
    p_instalment_id: item.instalmentId,
    p_kind: collected ? 'dd_confirmed' : 'dd_failed',
    // A failed collection moved no money, so it carries none. The derived
    // state reads the kind, not the amount.
    p_amount_cents: collected ? item.amountCents : 0,
    p_note: null,
    p_occurred_on: todayIso(),
  })
  if (error) throw error
}
